import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import {
  FUNDS_AGREEMENT_PATH,
  FUNDS_FIRM_PATH,
  LOGOUT,
  LOGOUT_REASONS,
  PUBLIC_ACCESS_PATH,
  PUBLIC_ERROR_PATH,
  PUBLIC_LOGIN_PATH,
  SECURITY_AUTHENTICATE_PATH,
  SECURITY_VERIFY_SESSION_PATH,
} from 'src/app/constants/NextGenConstants';
import { SECURITY_POLICIES_PATH } from 'src/app/constants/UserAPIConstants';
import { AuthenticateResponse } from 'src/app/models/AuthenticateResponse';
import { Policy } from 'src/app/models/Policy';
import { VerifySessionResponse } from 'src/app/models/VerifySessionResponse';
import { v4 as uuidv4 } from 'uuid';

/**
 * The AuthService handles authentication and authorization for NextGen.
 * It provides methods for login, logout, and policy retrieval.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private isAuthenticated: boolean = false;
  private isAuthorized: boolean = false;
  private readonly userPolicy: Policy = {
    name: '',
    subPolicies: [],
  };

  isUserAuthenticated(): boolean {
    return this.isAuthenticated;
  }

  isUserAuthorized(): boolean {
    return this.isAuthorized;
  }

  logout(resetUserPolicy = true): void {
    this.isAuthenticated = false;
    this.isAuthorized = false;
    if (resetUserPolicy) {
      this.userPolicy.subPolicies = [];
    }
  }

  /**
   * Returns a deep copy of a policy found within another policy or `undefined` if no matching policy is found.
   * If the `parentPolicy` argument is not provided, this method searches within the user's root policy.
   * If no arguments are provided, the user's root policy is returned.
   *
   * Ex: `getPolicy('SALES REPORTING.CREATE REPORTS')` searches the user's root policy
   * and will return the policy with the name `CREATE REPORTS` that is a sub-policy of the
   * `SALES REPORTING` policy.
   */
  getPolicy(policy = '', parentPolicy = this.userPolicy): Policy | undefined {
    if (policy.length === 0) {
      return JSON.parse(JSON.stringify(parentPolicy));
    }

    const firstPolicy = this.getFirstPolicy(policy);
    const matchedSubPolicy = parentPolicy.subPolicies.find(
      (subPolicy) => subPolicy.name === firstPolicy,
    );

    return matchedSubPolicy
      ? this.getPolicy(policy.slice(firstPolicy.length + 1), matchedSubPolicy)
      : undefined;
  }

  /**
   * Returns a boolean describing whether a policy exists within another policy.
   * If the `parentPolicy` argument is not provided, this method searches within the user's root policy.
   *
   * Ex: `hasPolicy('SALES REPORTING.CREATE REPORTS')` searches the user's root policy
   * and will return true if the policy with the name `CREATE REPORTS` is a sub-policy of the
   * `SALES REPORTING` policy or false otherwise.
   */
  hasPolicy(policy: string, parentPolicy = this.userPolicy): boolean {
    const firstPolicy = this.getFirstPolicy(policy);
    const matchedSubPolicy = parentPolicy.subPolicies.find(
      (subPolicy) => subPolicy.name === firstPolicy,
    );

    if (!matchedSubPolicy) {
      return false;
    }

    const remainingPolicy = policy.slice(firstPolicy.length + 1);
    return remainingPolicy.length > 0
      ? this.hasPolicy(remainingPolicy, matchedSubPolicy)
      : true;
  }

  /**
   * Returns a boolean describing whether a policy exists within another policy.
   * If the policy does not exist, a router navigation is performed to the provided route path.
   * If the `parentPolicy` argument is not provided, this method searches within the user's root policy.
   */
  hasPolicyElseRoute(
    policy: string,
    routePath: string,
    parentPolicy = this.userPolicy,
  ): boolean {
    if (!this.hasPolicy(policy, parentPolicy)) {
      this.router.navigateByUrl(routePath);
      return false;
    }
    return true;
  }

  /**
   * Attempts to log in the user by calling the /authenticate endpoint.
   *
   * HTTP status code is the source of truth:
   * - 200 OK = authentication succeeded
   * - 401 Unauthorized = session expired or invalid at the upstream AM layer; navigate to session-timeout page
   * - 403 Forbidden = access denied; reads authenticationType from response body to determine routing
   * - 409 Conflict = prerequisite not yet satisfied; reads authenticationType from response body to determine routing:
   *     - ENTITLEMENT_MISSING -> navigate to entitlement selection page
   *     - TERMS_NOT_ACCEPTED -> navigate to terms acceptance page
   *     - ACCESS_DENIED → navigate to access denied page
   * - 500 or missing/unknown reason -> default to error page (secure fail)
   *
   * @returns Observable<boolean> indicating login success.
   */
  login(entitlementGroup?: string): Observable<boolean> {
    this.logout();
    const params = entitlementGroup
      ? new HttpParams().set('entitlementGroup', entitlementGroup)
      : undefined;
    return this.http
      .post<AuthenticateResponse>(SECURITY_AUTHENTICATE_PATH, null, {
        headers: this.contentTypeHeader,
        params,
        observe: 'response',
      })
      .pipe(
        switchMap(() => {
          // 200 OK received; authentication succeeded
          this.isAuthenticated = true;
          this.isAuthorized = true;
          return this.refreshPolicies().pipe(
            tap((success) => {
              if (!success) {
                this.router.navigate([PUBLIC_ERROR_PATH]);
              }
            }),
          );
        }),
        catchError((error) => {
          // HTTP status code indicated failure.
          this.logout();

          if (error instanceof HttpErrorResponse) {
            if (error.status === 401) {
              // Session expired or invalid at the AM layer.
              this.router.navigate([PUBLIC_LOGIN_PATH], {
                queryParams: { ccview: LOGOUT, reason: LOGOUT_REASONS[1] },
              });
            } else if (error.status === 403 || error.status === 409) {
              // Authorization/prerequisite check failed — use typed response body reason.
              const authReason = error.error?.authenticationType ?? '';
              if (authReason === 'ENTITLEMENT_MISSING') {
                this.isAuthenticated = true;
                this.router.navigate([FUNDS_FIRM_PATH]);
              } else if (authReason === 'TERMS_NOT_ACCEPTED') {
                this.isAuthenticated = true;
                this.router.navigate([FUNDS_AGREEMENT_PATH]);
              } else if (authReason === 'ACCESS_DENIED') {
                this.router.navigate([PUBLIC_ACCESS_PATH]);
              } else {
                // Missing/unknown reason — default to error page (secure fail)
                this.router.navigate([PUBLIC_ERROR_PATH]);
              }
            } else {
              // Other errors
              this.router.navigate([PUBLIC_ERROR_PATH]);
            }
          }
          return of(false);
        }),
      );
  }

  /**
   * Attempts to log in the user by calling the /verifySession endpoint.
   * This endpoint only verifies an existing session. It is used on page refreshes
   * and new tabs after user has already logged in.
   */
  verifySession(): Observable<boolean> {
    this.logout(false);
    return this.http
      .get<VerifySessionResponse>(SECURITY_VERIFY_SESSION_PATH, {
        headers: this.contentTypeHeader,
      })
      .pipe(
        switchMap((response) => {
          if (!response.valid) {
            this.logout();
            return of(false);
          }
          this.isAuthenticated = true;
          this.isAuthorized = true;
          return this.userPolicy.subPolicies.length === 0
            ? this.refreshPolicies()
            : of(true);
        }),
        catchError(() => {
          this.logout();
          return of(false);
        }),
      );
  }

  /**
   * Fetch and store a user's policies. Logs out on failure.
   */
  refreshPolicies(): Observable<boolean> {
    this.userPolicy.subPolicies = [];
    if (!this.isAuthenticated || !this.isAuthorized) {
      return of(false);
    }
    return this.http
      .post<{
        policyElements: string[];
      }>(SECURITY_POLICIES_PATH, null, { headers: this.contentTypeHeader })
      .pipe(
        tap((response) => {
          const newUserPolicy: Policy = {
            name: '',
            subPolicies: [],
          };
          response?.policyElements?.forEach((newPolicyElement) =>
            this.addPolicy(newPolicyElement, newUserPolicy),
          );
          this.userPolicy.subPolicies = newUserPolicy.subPolicies;
        }),
        map(() => true),
        catchError(() => {
          this.logout();
          return of(false);
        }),
      );
  }

  /**
   * A utility function that adds a new policy to an existing policy.
   */
  private addPolicy(policy: string, parentPolicy: Policy) {
    if (policy.length === 0) {
      return;
    }

    const firstPolicy = this.getFirstPolicy(policy);
    let matchedSubPolicy = parentPolicy.subPolicies.find(
      (subPolicy) => subPolicy.name === firstPolicy,
    );

    if (matchedSubPolicy === undefined) {
      matchedSubPolicy = {
        name: firstPolicy,
        subPolicies: [],
      };
      parentPolicy.subPolicies.push(matchedSubPolicy);
    }

    this.addPolicy(policy.slice(firstPolicy.length + 1), matchedSubPolicy);
  }

  /**
   * A utility function that finds the first policy segment in a string of
   * policies separated by periods.
   * EX: "SALES REPORTING.CREATE REPORTS.ACTIVITY DASHBOARD" -> "SALES REPORTING"
   */
  private getFirstPolicy(policy: string) {
    const separatorIndex = policy.indexOf('.');
    return separatorIndex !== -1 ? policy.slice(0, separatorIndex) : policy;
  }

  /**
   * Returns HTTP headers with content type and a unique tracking ID.
   */
  private get contentTypeHeader(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'FID-LOG-TRACKING-ID': uuidv4(),
    });
  }
}
